import { useState } from 'react';
import { ShieldAlert, MessageSquareText, AlertTriangle, Loader2 } from 'lucide-react';
import { palette, admin, shadow, radii, fontFamily } from '../../styles/tokens';
import { useModerationQueue, type ModerateAction, type PendingReport } from './useModerationQueue';

// C2 · P6 — platform-admin moderation queue (standalone /moderation for v1; keep
// route-agnostic so it re-homes under a future Praetori console with near-zero
// rework). Admin tokens only — no vf/cb. Plain-English labels (no i18n runtime).

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-CA', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function ModerationPage() {
  const { reports, loading, error, moderate } = useModerationQueue();
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handle = async (reportId: string, action: ModerateAction) => {
    setActingId(reportId);
    setActionError(null);
    const res = await moderate(reportId, action);
    setActingId(null);
    if (!res.success) setActionError(res.error ?? 'Action failed.');
  };

  return (
    <div style={{ padding: 24, maxWidth: 880, margin: '0 auto', fontFamily }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <ShieldAlert size={22} color={palette.accent} strokeWidth={2} />
        <h1 style={{ fontSize: 22, fontWeight: 600, color: admin.header, margin: 0 }}>Moderation</h1>
      </div>
      <p style={{ fontSize: 14, color: admin.caption, margin: '0 0 20px' }}>
        Pending message &amp; user reports flagged by clients.
      </p>

      {actionError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', marginBottom: 16,
          borderRadius: radii.sm, background: palette.redWash,
          border: `1px solid ${palette.red}`, color: palette.red, fontSize: 13,
        }}>
          <AlertTriangle size={16} strokeWidth={2} />
          {actionError}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <Loader2 size={24} color={palette.g400} style={{ animation: 'mod-spin 0.8s linear infinite' }} />
          <style>{`@keyframes mod-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : error ? (
        <EmptyState title="Couldn't load reports" hint={error} tone="error" />
      ) : reports.length === 0 ? (
        <EmptyState title="No pending reports" hint="Flags raised by clients will appear here." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} acting={actingId === r.id} onAction={handle} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportCard({
  report, acting, onAction,
}: {
  report: PendingReport;
  acting: boolean;
  onAction: (id: string, action: ModerateAction) => void;
}) {
  const canRemove = report.reported_message_id !== null;
  return (
    <div style={{
      background: admin.surface, border: `1px solid ${admin.border}`,
      borderRadius: admin.cardRadius, boxShadow: shadow.xs, padding: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <span style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
          color: palette.amber, background: palette.amberWash,
          padding: '3px 8px', borderRadius: radii.full,
        }}>
          {report.report_type}
        </span>
        <span style={{ fontSize: 12, color: admin.caption }}>{fmtTime(report.created_at)}</span>
      </div>

      <div style={{ fontSize: 14, fontWeight: 600, color: admin.header, marginBottom: 2 }}>
        {report.reporter_name ?? 'Someone'}
        <span style={{ color: admin.muted, fontWeight: 400 }}> reported </span>
        {report.reported_user_name ?? 'a message'}
      </div>

      {report.description && (
        <div style={{ fontSize: 13, color: admin.body, marginBottom: report.message_content ? 10 : 0 }}>
          {report.description}
        </div>
      )}

      {report.message_content && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          background: palette.bg, border: `1px solid ${admin.border}`,
          borderRadius: radii.sm, padding: '8px 10px', marginTop: 6,
        }}>
          <MessageSquareText size={14} color={palette.g400} strokeWidth={2} style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, color: admin.body, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {report.message_content}
            </div>
            <div style={{ fontSize: 11, color: admin.muted, marginTop: 2 }}>
              Flagged message · {fmtTime(report.message_created_at)}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button
          type="button"
          disabled={acting}
          onClick={() => onAction(report.id, 'dismiss')}
          style={{
            padding: '7px 14px', borderRadius: radii.sm, fontSize: 13, fontWeight: 500,
            fontFamily: 'inherit', cursor: acting ? 'default' : 'pointer',
            border: `1px solid ${admin.border}`, background: palette.white, color: admin.body,
            opacity: acting ? 0.6 : 1,
          }}
        >
          Dismiss
        </button>
        {canRemove && (
          <button
            type="button"
            disabled={acting}
            onClick={() => onAction(report.id, 'remove')}
            style={{
              padding: '7px 14px', borderRadius: radii.sm, fontSize: 13, fontWeight: 600,
              fontFamily: 'inherit', cursor: acting ? 'default' : 'pointer',
              border: `1px solid ${palette.red}`, background: palette.red, color: palette.white,
              opacity: acting ? 0.6 : 1,
            }}
          >
            Remove message
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ title, hint, tone }: { title: string; hint: string; tone?: 'error' }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '64px 24px',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 28, marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: tone === 'error' ? palette.redWash : palette.accentWash,
        color: tone === 'error' ? palette.red : palette.accent,
      }}>
        <ShieldAlert size={26} strokeWidth={1.5} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: admin.header, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 14, color: admin.caption, maxWidth: 360, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );
}
